/**
 * Dinero: una sola forma de leer los importes de un pago.
 *
 * La API expone casi todo dos veces (`amount` en pesos y `amountCents` en
 * centavos, que es lo que guarda la BD). Antes cada componente resolvía esa
 * ambigüedad por su cuenta; aquí se resuelve una vez.
 *
 * Regla que manda en todo el archivo: **no se inventan números**. Si el backend
 * no mandó un dato, el helper devuelve `null` y la vista pinta "—". Nunca se
 * deriva una comisión con el porcentaje vigente para rellenar un hueco: los
 * pagos ya cobrados llevan el porcentaje del día en que se cobraron.
 */

import { Payment } from './payment.model';

/** Resuelve un importe que la API expone en pesos y/o en centavos. */
export function money(
  value: number | null | undefined,
  cents?: number | null | undefined,
): number | null {
  if (value !== null && value !== undefined) return value;
  if (cents !== null && cents !== undefined) return cents / 100;
  return null;
}


/** Igual que `money`, pero con 0 en vez de `null` (para sumas y comparaciones). */
export function moneyOr0(
  value: number | null | undefined,
  cents?: number | null | undefined,
): number {
  return money(value, cents) ?? 0;
}

// ---------- Lecturas de un pago ----------

/** Total cobrado al comprador. */
export function paymentAmount(p: Payment): number {
  return moneyOr0(p.amount, p.amountCents);
}

/** Comisión de la app (la paga el comprador). La API la nombra de 4 maneras. */
export function paymentAppFee(p: Payment): number | null {
  return (
    money(p.fee, p.feeCents) ??
    money(p.commission, p.commissionCents) ??
    money(p.breakdown?.appFee, p.breakdown?.appFeeCents)
  );
}

/** Precio base con el que el vendedor anunció la pieza. */
export function paymentBasePrice(p: Payment): number | null {
  return p.offerPrice ?? money(p.breakdown?.piece, p.breakdown?.pieceCents);
}

export function paymentShipping(p: Payment): number | null {
  return money(p.breakdown?.shipping, p.breakdown?.shippingCents);
}

/** Lo devuelto al comprador hasta ahora. */
export function paymentRefunded(p: Payment): number {
  return moneyOr0(p.refunded, p.refundedCents);
}

/** Retenido por contracargos abiertos o perdidos. */
export function paymentDisputed(p: Payment): number {
  return moneyOr0(p.disputed, p.disputedCents);
}

/** Lo que aún se le podría devolver al comprador. */
export function paymentRemaining(p: Payment): number {
  return moneyOr0(p.remainingRefundable, p.remainingRefundableCents);
}

/** Lo que Stripe descontó por procesar el cobro. */
export function paymentStripeFee(p: Payment): number | null {
  return p.stripeFee ?? money(p.breakdown?.stripeFee, p.breakdown?.stripeFeeCents);
}

/** Lo que realmente entró a la cuenta de Stripe (total − su comisión). */
export function paymentStripeNet(p: Payment): number | null {
  return money(p.breakdown?.stripeNet, p.breakdown?.stripeNetCents);
}

/** Comisión que se le descuenta al vendedor. */
export function paymentSellerCommission(p: Payment): number | null {
  return money(p.breakdown?.sellerCommission, p.breakdown?.sellerCommissionCents);
}

/**
 * Depósito al vendedor. Si el payout ya existe manda su importe real (puede
 * estar reducido por reembolsos); si no, el previsto en el desglose.
 */
export function paymentSellerPayout(p: Payment): number | null {
  return (
    money(p.payout?.amount, p.payout?.amountCents) ??
    money(p.breakdown?.sellerPayout, p.breakdown?.sellerPayoutCents)
  );
}

/** Lo que le queda a la plataforma de este cobro. */
export function paymentPlatformRevenue(p: Payment): number | null {
  return money(p.breakdown?.platformRevenue, p.breakdown?.platformRevenueCents);
}

// ---------- De dónde viene el pago ----------

/**
 * Vehículo (subasta) del pago. El backend lo manda en `relations`; se acepta
 * también en la raíz por si otro endpoint lo aplana.
 */
export function paymentAuctionId(p: Payment): number | null {
  return numberOrNull(p.relations?.auctionId ?? p['auctionId']);
}

/** Pieza solicitada del pago. */
export function paymentRequestedPartId(p: Payment): number | null {
  return numberOrNull(p.relations?.requestedPartId ?? p['requestedPartId']);
}

/** Oferta ganadora del pago. */
export function paymentOfferId(p: Payment): number | null {
  return numberOrNull(p.relations?.offerId ?? p.offerId);
}

/** Id utilizable: descarta null, vacíos y ceros (MySQL devuelve 0 y strings). */
function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// ---------- Identificación ----------

export function paymentConcept(p: Payment): string {
  return p.partTitle || p.title || '—';
}

export function paymentBuyer(p: Payment): string {
  return p.buyerName || p.buyerEmail || (p.userId != null ? `#${p.userId}` : '—');
}

export function paymentSeller(p: Payment): string {
  return p.sellerName || p.sellerEmail || (p.sellerUserId != null ? `#${p.sellerUserId}` : '—');
}

/** Estado del pedido (la API lo manda en camel o snake según el endpoint). */
export function paymentOrderStatus(p: Payment): string {
  return p.orderStatus ?? p.order_status ?? p.status ?? '';
}

export function paymentIntentId(p: Payment): string | null {
  return p.stripePaymentIntentId ?? p.paymentIntentId ?? null;
}

/** Se puede reembolsar: el cobro sigue vivo y queda saldo por devolver. */
export function canRefundPayment(p: Payment): boolean {
  return (p.status === 'succeeded' || p.status === 'partially_refunded') && paymentRemaining(p) > 0;
}

// ---------- Cascada del dinero ----------

/**
 * `in` suma hacia el total cobrado · `out` sale de él · `total` es el corte del
 * comprador · `result` es a dónde acabó yendo el dinero.
 */
export type MoneyFlowTone = 'in' | 'out' | 'total' | 'result';

/** Un renglón de la cascada "a dónde fue cada peso". */
export interface MoneyFlowRow {
  key: string;
  label: string;
  /** Aclaración breve bajo la etiqueta. */
  hint?: string;
  /** Porcentaje con el que se calculó el importe, cuando la API lo manda. */
  percent?: number | null;
  /** `null` = el backend no mandó el dato; la vista pinta "—". */
  amount: number | null;
  tone: MoneyFlowTone;
  /**
   * El importe se cobró con el porcentaje de ese día: cambiar el tarifario
   * hoy no lo altera. Se marca para que nadie lo compare con el vigente.
   */
  historic?: boolean;
  /** De dónde sale el importe, en palabras: «8% de $1,000.00 (precio base)». */
  formula?: string;
}

/**
 * Desglose exacto de un pago, de lo que ofertó el vendedor a lo que acabó
 * recibiendo cada parte. Todos los importes salen del backend tal cual.
 */
export function buildMoneyFlow(p: Payment): MoneyFlowRow[] {
  const b = p.breakdown ?? {};
  const base = paymentBasePrice(p);
  const rows: MoneyFlowRow[] = [
    {
      key: 'piece',
      label: 'Lo que pidió el vendedor por la pieza',
      hint: 'Precio base con el que anunció la oferta',
      amount: paymentBasePrice(p),
      tone: 'in',
    },
    {
      key: 'appFee',
      label: 'Comisión de la app',
      hint: 'La paga el comprador, encima del precio base',
      percent: b.appFeePercent ?? null,
      amount: paymentAppFee(p),
      tone: 'in',
      historic: true,
      formula: percentFormula(b.appFeePercent, base, paymentAppFee(p)),
    },
    {
      key: 'shipping',
      label: 'Envío',
      hint: p.shippingIncurred ? 'La guía ya se generó: el costo con DHL ya se incurrió' : undefined,
      amount: paymentShipping(p),
      tone: 'in',
    },
    {
      key: 'total',
      label: 'Total que pagó el comprador',
      amount: paymentAmount(p),
      tone: 'total',
    },
  ];

  const refunded = paymentRefunded(p);
  if (refunded > 0) {
    rows.push({
      key: 'refunded',
      label: 'Devuelto al comprador',
      hint: `Quedan ${formatMoney(paymentRemaining(p))} que aún se podrían devolver`,
      amount: -refunded,
      tone: 'out',
    });
  }

  const disputed = paymentDisputed(p);
  if (disputed > 0) {
    rows.push({
      key: 'disputed',
      label: 'Retenido por contracargo',
      hint: 'Stripe retiró estos fondos mientras se resuelve la disputa',
      amount: -disputed,
      tone: 'out',
    });
  }

  const stripeFee = paymentStripeFee(p);
  rows.push({
    key: 'stripeFee',
    label: 'Comisión de Stripe',
    hint:
      stripeFee === null
        ? 'Aún sin capturar de Stripe'
        : 'Lo que cobra Stripe por procesar; nunca llega al banco',
    amount: stripeFee === null ? null : -stripeFee,
    tone: 'out',
  });

  const sellerCommission = paymentSellerCommission(p);
  rows.push({
    key: 'sellerCommission',
    label: 'Comisión al vendedor',
    hint: 'Se le descuenta de su parte',
    percent: b.sellerCommissionPercent ?? null,
    amount: sellerCommission === null ? null : -sellerCommission,
    tone: 'out',
    historic: true,
    formula: percentFormula(b.sellerCommissionPercent, base, sellerCommission),
  });

  rows.push({
    key: 'sellerPayout',
    label: 'Depósito al vendedor',
    hint: payoutHint(p),
    amount: paymentSellerPayout(p),
    tone: 'result',
  });

  rows.push({
    key: 'platformRevenue',
    label: 'Comisiones para la plataforma',
    // El backend suma comisión de la app + comisión del vendedor y no resta la
    // de Stripe, así que decir "ganancia" aquí sería inflarla.
    hint: 'Las dos comisiones juntas, antes de descontar la de Stripe',
    amount: paymentPlatformRevenue(p),
    tone: 'result',
  });

  return rows;
}

/**
 * De dónde sale una comisión: «8% de $1,000.00 (precio base) = $80.00».
 *
 * El backend suma a cada comisión un cargo fijo por operación
 * (`APP_FEE_FIXED_CENTS` / `SELLER_COMMISSION_FIXED_CENTS`, 0 por defecto) que
 * no se guarda en el pago. Si la comisión registrada es mayor que el
 * porcentaje, esa diferencia es el cargo fijo y se dice así; si es MENOR, no
 * hay cargo que lo explique y se avisa.
 */
function percentFormula(
  percent: number | null | undefined,
  base: number | null,
  amount: number | null,
): string | undefined {
  if (percent === null || percent === undefined || base === null) return undefined;
  const fromPercent = round2((base * percent) / 100);
  const text = `${percent}% de ${formatMoney(base)} (precio base) = ${formatMoney(fromPercent)}`;
  if (amount === null) return text;
  const extra = round2(amount - fromPercent);
  if (extra > CENT) return `${text} + ${formatMoney(extra)} de cargo fijo`;
  if (extra < -CENT) return `${text} · ⚠ se registró ${formatMoney(amount)}, menos de lo que da el porcentaje`;
  return text;
}

// ---------- ¿Cuadra? ----------

/** Una comprobación aritmética del desglose. */
export interface MoneyCheck {
  key: string;
  /** Qué se comprueba, en palabras. */
  label: string;
  /** La operación con los importes reales: «$1,000.00 + $80.00 + $150.00». */
  operation: string;
  expected: number;
  actual: number;
  ok: boolean;
  /** `actual − expected`; 0 cuando cuadra. */
  difference: number;
}

/** Margen de redondeo: el backend redondea cada importe al centavo. */
const CENT = 0.01;

/**
 * Comprueba que los importes del desglose se sumen entre sí.
 *
 * Solo usa datos que mandó el backend: si falta cualquiera de los sumandos, la
 * comprobación no se hace (no se rellena con ceros ni con el tarifario vigente).
 *
 * Los porcentajes NO se comprueban aquí: el backend les suma un cargo fijo que
 * no viaja con el pago, así que se explican en la fórmula de cada renglón
 * (`percentFormula`). Estas comprobaciones son sumas exactas, independientes
 * del tarifario, y deben cuadrar siempre al centavo.
 */
export function checkMoneyFlow(p: Payment): MoneyCheck[] {
  const b = p.breakdown ?? {};
  const checks: MoneyCheck[] = [];
  const base = paymentBasePrice(p);
  const appFee = paymentAppFee(p);
  const shipping = paymentShipping(p);
  const total = paymentAmount(p);
  const sellerCommission = paymentSellerCommission(p);
  // El depósito PREVISTO: el real puede haberse reducido por reembolsos.
  const plannedPayout = money(b.sellerPayout, b.sellerPayoutCents);
  const platform = paymentPlatformRevenue(p);

  const push = (key: string, label: string, operation: string, expected: number, actual: number) => {
    const difference = round2(actual - expected);
    checks.push({ key, label, operation, expected, actual, difference, ok: Math.abs(difference) <= CENT });
  };

  if (base !== null && appFee !== null && shipping !== null) {
    push(
      'total',
      'Precio base + comisión de la app + envío = total cobrado',
      `${formatMoney(base)} + ${formatMoney(appFee)} + ${formatMoney(shipping)}`,
      round2(base + appFee + shipping),
      total,
    );
  }

  if (base !== null && sellerCommission !== null && plannedPayout !== null) {
    push(
      'payout',
      'Precio base − comisión al vendedor = depósito al vendedor',
      `${formatMoney(base)} − ${formatMoney(sellerCommission)}`,
      round2(base - sellerCommission),
      plannedPayout,
    );
  }

  if (appFee !== null && sellerCommission !== null && platform !== null) {
    push(
      'platform',
      'Comisión de la app + comisión al vendedor = comisiones para la plataforma',
      `${formatMoney(appFee)} + ${formatMoney(sellerCommission)}`,
      round2(appFee + sellerCommission),
      platform,
    );
  }

  if (plannedPayout !== null && platform !== null && shipping !== null) {
    push(
      'split',
      'Vendedor + plataforma + envío = total cobrado (no sobra ni falta un peso)',
      `${formatMoney(plannedPayout)} + ${formatMoney(platform)} + ${formatMoney(shipping)}`,
      round2(plannedPayout + platform + shipping),
      total,
    );
  }

  return checks;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Explica el depósito: si ya se pagó, si sigue pendiente o si se ajustó. */
function payoutHint(p: Payment): string | undefined {
  const payout = p.payout;
  if (!payout) return 'Todavía no se ha generado el depósito';
  const original = money(null, payout.originalAmountCents);
  const current = money(payout.amount, payout.amountCents);
  if (original !== null && current !== null && original !== current) {
    return `Originalmente ${formatMoney(original)}; se ajustó por reembolsos`;
  }
  if (payout.paidAt) return 'Ya transferido al vendedor';
  if (payout.onHold === true || payout.onHold === 1) return 'Retenido por un administrador';
  return 'Pendiente de liberar';
}

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

/** Importe en pesos para textos sueltos (los templates usan el pipe `currency`). */
export function formatMoney(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : MXN.format(value);
}
